import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FaArrowDown,
  FaArrowLeft,
  FaArrowRight,
  FaPause,
  FaPlay,
  FaRedoAlt,
  FaSyncAlt,
} from "react-icons/fa";
import GameRoomBackButton from "../GameRoomBackButton";
import {
  BOARD_COLS,
  BOARD_ROWS,
  PIECES,
  clearLines,
  createBag,
  createEmptyBoard,
  dropDelayForLevel,
  getCells,
  getGhostRow,
  isValidPosition,
  levelForLines,
  mergePiece,
  rotateWithKick,
  scoreForClear,
  spawnPiece,
} from "./blockDropEngine";
import "./BlockDrop.css";
import { submitSoloGameResult } from "../../../services/games/gameService";

const HIGH_SCORE_KEY = "hobbli:block-drop:high-score";

const loadHighScore = () => {
  try {
    return Number(window.localStorage.getItem(HIGH_SCORE_KEY)) || 0;
  } catch {
    return 0;
  }
};

function MiniPiece({ type, label }) {
  const cells = type ? PIECES[type].rotations[0] : [];
  return (
    <div className="block-drop-mini-wrap">
      <span>{label}</span>
      <div className="block-drop-mini-grid" aria-label={`${label}: ${type || "empty"}`}>
        {Array.from({ length: 16 }, (_, index) => {
          const row = Math.floor(index / 4);
          const col = index % 4;
          const filled = cells.some(([r, c]) => r === row && c === col);
          return (
            <div
              key={index}
              className={`block-drop-mini-cell ${filled ? `piece-${type}` : ""}`}
            />
          );
        })}
      </div>
    </div>
  );
}

export default function BlockDrop({ currentUser }) {
  const bagRef = useRef([]);
  const queueRef = useRef([]);
  const holdLockedRef = useRef(false);
  const gameStartedAtRef = useRef(null);
  const resultSubmittedRef = useRef(false);
  const [ showGhost, setShowGhost ] = useState(false);
  const drawPiece = useCallback(() => {
    if (!bagRef.current.length) bagRef.current = createBag();
    return bagRef.current.shift();
  }, []);

  const fillQueue = useCallback(() => {
    while (queueRef.current.length < 5) queueRef.current.push(drawPiece());
  }, [drawPiece]);

  const [board, setBoard] = useState(createEmptyBoard);
  const [current, setCurrent] = useState(null);
  const [queue, setQueue] = useState([]);
  const [hold, setHold] = useState(null);
  const [score, setScore] = useState(0);
  const [lines, setLines] = useState(0);
  const [highScore, setHighScore] = useState(loadHighScore);
  const [status, setStatus] = useState("ready"); // ready | playing | paused | over

  const level = levelForLines(lines);

  const syncQueue = useCallback(() => {
    fillQueue();
    setQueue([...queueRef.current]);
  }, [fillQueue]);

  const nextFromQueue = useCallback(() => {
    fillQueue();
    const type = queueRef.current.shift();
    fillQueue();
    setQueue([...queueRef.current]);
    return spawnPiece(type);
  }, [fillQueue]);

  const startGame = useCallback(() => {
    bagRef.current = createBag();
    queueRef.current = [];
    holdLockedRef.current = false;
    const freshBoard = createEmptyBoard();
    setBoard(freshBoard);
    setScore(0);
    setLines(0);
    setHold(null);
    setStatus("playing");
    gameStartedAtRef.current = Date.now();
    resultSubmittedRef.current = false;
    const piece = nextFromQueue();
    setCurrent(piece);
    syncQueue();
  }, [nextFromQueue, syncQueue]);

  const finishGame = useCallback((finalScore) => {
    setStatus("over");
    setHighScore((previous) => {
      const next = Math.max(previous, finalScore);
      try {
        window.localStorage.setItem(HIGH_SCORE_KEY, String(next));
      } catch {
        // Local storage is optional; the game remains playable without it.
      }
      return next;
    });

    if (currentUser?.id && !resultSubmittedRef.current) {
      resultSubmittedRef.current = true;
      const durationMs = gameStartedAtRef.current
        ? Math.max(250, Date.now() - gameStartedAtRef.current)
        : null;

      submitSoloGameResult({
        gameKey: "block-drop",
        result: "completed",
        score: Math.max(0, Math.round(finalScore || 0)),
        durationMs,
        stats: { lines, level },
      }).catch((error) => {
        console.warn("Could not save Block Drop result to Hobbli:", error);
      });
    }
  }, [currentUser?.id, level, lines]);

  const lockPiece = useCallback(
    (pieceToLock, placementBonus = 0) => {
      setBoard((previousBoard) => {
        const merged = mergePiece(previousBoard, pieceToLock);
        const baseScore = score + placementBonus;
        if (merged.toppedOut) {
          if (placementBonus) setScore(baseScore);
          finishGame(baseScore);
          return merged.board;
        }

        const result = clearLines(merged.board);
        const nextLines = lines + result.cleared;
        const earned = scoreForClear(result.cleared, level);
        const nextScore = baseScore + earned;

        if (result.cleared) {
          setLines(nextLines);
        }
        if (placementBonus || result.cleared) {
          setScore(nextScore);
        }

        const nextPiece = nextFromQueue();
        holdLockedRef.current = false;

        if (!isValidPosition(result.board, nextPiece)) {
          finishGame(nextScore);
          setCurrent(nextPiece);
          return result.board;
        }

        setCurrent(nextPiece);
        return result.board;
      });
    },
    [finishGame, level, lines, nextFromQueue, score]
  );

  const move = useCallback(
    (deltaRow, deltaCol, softDrop = false) => {
      if (status !== "playing" || !current) return;
      const candidate = {
        ...current,
        row: current.row + deltaRow,
        col: current.col + deltaCol,
      };

      if (isValidPosition(board, candidate)) {
        setCurrent(candidate);
        if (softDrop && deltaRow > 0) setScore((value) => value + 1);
      } else if (deltaRow > 0 && deltaCol === 0) {
        lockPiece(current);
      }
    },
    [board, current, lockPiece, status]
  );

  const rotate = useCallback(() => {
    if (status !== "playing" || !current) return;
    setCurrent(rotateWithKick(board, current, 1));
  }, [board, current, status]);

  const hardDrop = useCallback(() => {
    if (status !== "playing" || !current) return;
    const ghostRow = getGhostRow(board, current);
    const distance = Math.max(0, ghostRow - current.row);
    const dropped = { ...current, row: ghostRow };
    lockPiece(dropped, distance * 2);
  }, [board, current, lockPiece, status]);

  const holdPiece = useCallback(() => {
    if (status !== "playing" || !current || holdLockedRef.current) return;
    holdLockedRef.current = true;

    if (!hold) {
      setHold(current.type);
      setCurrent(nextFromQueue());
      return;
    }

    const previousHold = hold;
    setHold(current.type);
    setCurrent(spawnPiece(previousHold));
  }, [current, hold, nextFromQueue, status]);

  const togglePause = useCallback(() => {
    setStatus((value) => {
      if (value === "playing") return "paused";
      if (value === "paused") return "playing";
      return value;
    });
  }, []);

  useEffect(() => {
    if (status !== "playing" || !current) return undefined;
    const timer = window.setInterval(() => move(1, 0), dropDelayForLevel(level));
    return () => window.clearInterval(timer);
  }, [current, level, move, status]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      const keys = ["ArrowLeft", "ArrowRight", "ArrowDown", "ArrowUp", " ", "c", "C", "p", "P"];
      if (keys.includes(event.key)) event.preventDefault();

      if (event.key === "p" || event.key === "P") {
        togglePause();
        return;
      }
      if (status !== "playing") return;

      if (event.key === "ArrowLeft") move(0, -1);
      else if (event.key === "ArrowRight") move(0, 1);
      else if (event.key === "ArrowDown") move(1, 0, true);
      else if (event.key === "ArrowUp") rotate();
      else if (event.key === " ") hardDrop();
      else if (event.key === "c" || event.key === "C") holdPiece();
    };

    window.addEventListener("keydown", handleKeyDown, { passive: false });
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [hardDrop, holdPiece, move, rotate, status, togglePause]);

  const displayBoard = useMemo(() => {
    const cells = board.map((row) => row.map((value) => ({ value, ghost: false })));
    if (!current) return cells;

    if (showGhost) {
      const ghost = {
        ...current,
        row: getGhostRow(board, current),
      };

      getCells(ghost).forEach(([row, col]) => {
        if (
          row >= 0 &&
          row < BOARD_ROWS &&
          col >= 0 &&
          col < BOARD_COLS &&
          !cells[row][col].value
        ) {
          cells[row][col] = {
            value: current.type,
            ghost: true,
          };
        }
      });
    }

    getCells(current).forEach(([row, col]) => {
      if (row >= 0 && row < BOARD_ROWS && col >= 0 && col < BOARD_COLS) {
        cells[row][col] = { value: current.type, ghost: false };
      }
    });
    return cells;
  }, [board, current, showGhost]);

  return (
    <div className="block-drop-page">
      <section className="block-drop-shell">
        <header className="block-drop-header">
          <GameRoomBackButton />
          <div className="block-drop-title-wrap">
            <span>Hobbli Game Room</span>
            <h1>Block Drop</h1>
            <p>Stack clean. Clear lines. Keep the board alive.</p>
          </div>
          <div className="block-drop-high-score">
            <span>High Score</span>
            <strong>{highScore.toLocaleString()}</strong>
          </div>
        </header>

        <div className="block-drop-layout">
          <aside className="block-drop-side block-drop-side-left">
            <MiniPiece type={hold} label="Hold" />
            <div className="block-drop-help-card">
              <h2>Controls</h2>
              <p><strong>← →</strong> Move</p>
              <p><strong>↓</strong> Soft drop</p>
              <p><strong>↑</strong> Rotate</p>
              <p><strong>Space</strong> Hard drop</p>
              <p><strong>C</strong> Hold</p>
              <p><strong>P</strong> Pause</p>
            </div>
            <div className="block-drop-help-card">
            <h2>Options</h2>

            <label className="block-drop-option">
              <span>Ghost Piece</span>
              <input
                type="checkbox"
                checked={showGhost}
                onChange={(event) =>
                  setShowGhost(event.target.checked)
                }
              />
            </label>
          </div>
          </aside>

          <main className="block-drop-game-area">
            <div className="block-drop-scorebar">
              <div><span>Score</span><strong>{score.toLocaleString()}</strong></div>
              <div><span>Lines</span><strong>{lines}</strong></div>
              <div><span>Level</span><strong>{level}</strong></div>
            </div>

            <div className="block-drop-board-wrap">
              <div className="block-drop-board" role="grid" aria-label="Block Drop board">
                {displayBoard.flatMap((row, r) =>
                  row.map((cell, c) => (
                    <div
                      key={`${r}-${c}`}
                      className={`block-drop-cell ${cell.value ? `piece-${cell.value}` : ""} ${cell.ghost ? "is-ghost" : ""}`}
                      role="gridcell"
                    />
                  ))
                )}
              </div>

              {(status === "ready" || status === "paused" || status === "over") && (
                <div className="block-drop-overlay">
                  {status === "ready" && <h2>Ready to Drop?</h2>}
                  {status === "paused" && <h2>Paused</h2>}
                  {status === "over" && <><h2>Game Over</h2><p>Score: {score.toLocaleString()}</p></>}
                  <button type="button" onClick={status === "paused" ? togglePause : startGame}>
                    {status === "paused" ? <FaPlay /> : <FaRedoAlt />}
                    {status === "paused" ? "Resume" : status === "over" ? "Play Again" : "Start Game"}
                  </button>
                </div>
              )}
            </div>

            <div className="block-drop-mobile-controls" aria-label="Touch controls">
              <button type="button" onClick={() => move(0, -1)} aria-label="Move left"><FaArrowLeft /></button>
              <button type="button" onClick={rotate} aria-label="Rotate"><FaSyncAlt /></button>
              <button type="button" onClick={() => move(0, 1)} aria-label="Move right"><FaArrowRight /></button>
              <button type="button" onClick={() => move(1, 0, true)} aria-label="Soft drop"><FaArrowDown /></button>
              <button type="button" className="wide" onClick={hardDrop}>Drop</button>
              <button type="button" className="wide" onClick={holdPiece}>Hold</button>
            </div>


            <div className="block-drop-actions">
              <button type="button" onClick={togglePause} disabled={status !== "playing" && status !== "paused"}>
                {status === "paused" ? <FaPlay /> : <FaPause />}
                {status === "paused" ? "Resume" : "Pause"}
              </button>
              <button type="button" onClick={startGame}><FaRedoAlt /> New Game</button>
            </div>
          </main>

          <aside className="block-drop-side block-drop-side-right">
            <MiniPiece type={queue[0]} label="Next" />
            <div className="block-drop-next-list">
              {queue.slice(1, 4).map((type, index) => (
                <MiniPiece key={`${type}-${index}`} type={type} label={`+${index + 2}`} />
              ))}
            </div>
            <div className="block-drop-help-card">
              <h2>Scoring</h2>
              <p>Clear multiple lines together for bigger bonuses.</p>
              <p>Soft and hard drops add small placement bonuses.</p>
              <p>Every 10 lines raises the level and speeds things up.</p>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
